import { create } from 'zustand'
import type { SpaceType } from '@/types'
import { useUploadStore } from './useUploadStore'

export type UploadDrawerMode = 'createApp' | 'newVersion' | 'edit'

interface UploadDrawerState {
  open: boolean
  mode: UploadDrawerMode | null
  app_id: string | null
  version_id: string | null
  space_id: string | null
  /** 创建入口的空间类型 */
  space_type: SpaceType | null
  /** 空间后台「创建应用」入口：分发方式锁空间自建、所属空间只读 */
  lockSpaceContext: boolean
  openCreateApp: (opts?: {
    space_id?: string
    space_type?: SpaceType | null
    lockSpaceContext?: boolean
  }) => void
  openNewVersion: (app_id: string) => void
  openEdit: (app_id: string, version_id: string) => void
  /** 创建应用解析后回填真实 app_id，保持抽屉打开 */
  bindAppId: (app_id: string) => void
  /** 新建草稿后回填 version_id */
  bindVersionId: (version_id: string) => void
  /** 仅关闭抽屉；上下文等 afterOpenChange 再清，避免关闭动画中标题闪变 */
  close: () => void
  /** 抽屉完全关闭后清理上下文 */
  reset: () => void
}

/** 只清残留（失败 / 已完成）会话；仍在上传或解析时保留 */
function resetUploadSession() {
  const { tasks, clear } = useUploadStore.getState()
  if (tasks.some((x) => x.status === 'uploading' || x.status === 'parsing')) return
  clear()
}

export const useUploadDrawerStore = create<UploadDrawerState>((set) => ({
  open: false,
  mode: null,
  app_id: null,
  version_id: null,
  space_id: null,
  space_type: null,
  lockSpaceContext: false,
  openCreateApp: (opts) => {
    resetUploadSession()
    set({
      open: true,
      mode: 'createApp',
      app_id: null,
      version_id: null,
      space_id: opts?.space_id ?? null,
      space_type: opts?.space_type ?? null,
      lockSpaceContext: opts?.lockSpaceContext ?? false,
    })
  },
  openNewVersion: (app_id) => {
    resetUploadSession()
    set({
      open: true,
      mode: 'newVersion',
      app_id,
      version_id: null,
      space_id: null,
      space_type: null,
      lockSpaceContext: false,
    })
  },
  openEdit: (app_id, version_id) => {
    resetUploadSession()
    set({
      open: true,
      mode: 'edit',
      app_id,
      version_id,
      space_id: null,
      space_type: null,
      lockSpaceContext: false,
    })
  },
  bindAppId: (app_id) => set({ app_id }),
  bindVersionId: (version_id) => set({ version_id }),
  close: () => set({ open: false }),
  reset: () =>
    set({
      mode: null,
      app_id: null,
      version_id: null,
      space_id: null,
      space_type: null,
      lockSpaceContext: false,
    }),
}))
